import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import type { GridApi } from "ag-grid-community";
import {
  CheckIcon,
  ExternalLinkIcon,
  HelpCircleIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  Table2Icon,
  Trash2Icon,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import CompactEvaluatorCards from "./CompactEvaluatorCards";
import CreateTableDialog from "./CreateTableDialog";
import EvaluateAdHocSheet from "./EvaluateAdHocSheet";
import EmptyEvaluateState from "./EmptyEvaluateState";
import EvaluatorBreakdownDialog from "./EvaluatorBreakdownDialog";
import ImportedSheetEditor from "./ImportedSheetEditor";
import MissingPromptTemplateNotice from "./MissingPromptTemplateNotice";
import PromotePromptVersionDialog, { UsePromptInTableAction } from "./PromotePromptVersion";
import ReferenceSheetDialog from "./ReferenceSheetDialog";
import EvaluateInputConfiguration from "./InputConfiguration";
import TemplateOverrideSelect from "./TemplateOverrideSelect";
import { useEvaluateScope } from "./useEvaluateScope";
import {
  type EvaluateAdHocRow,
  getEffectiveInputVariableSets,
  getEvaluateRunStaleState,
  getVariableNames,
  isEvaluateInputColumn,
  PROMPT_TEMPLATE_COLUMN_TYPE,
  scoreLabel,
  verdictClassName,
} from "./evaluate-drawer-utils";

import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import RunButton from "@/components/ui/RunButton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { EntityIcon } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType, usePromptRegistry } from "@/queries";
import {
  PlaygroundEvaluateSheetReference,
  type PlaygroundEvaluateExportPrompt,
  type PlaygroundEvaluateStateResponse,
  playgroundEvaluateMutationKeys,
  playgroundEvaluateQueryKeys,
  useCancelEvaluateRun,
  useCreatePlaygroundEvaluateDraft,
  useDeleteEvaluateSheetReference,
  useExportPlaygroundEvaluateDraft,
  useEvaluateRunDetail,
  useEvaluateSheetGrid,
  usePatchPlaygroundEvaluateSettings,
  usePlaygroundEvaluateState,
  usePromotePlaygroundPromptVersion,
  useRunEvaluateSheetReference,
} from "@/queries/playground/evaluate";
import type { ScorecardViewState } from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/types";
import ScorecardHeaderCard from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardHeaderCard";
import { preloadSmartTablePage } from "@/routes/smart-table/loadSmartTablePage";
import { useConfirm } from "@/context/confirm-context";
import { ToastType } from "@/enums";
import { cn } from "@/lib/utils";
import { getVariableValueIsFilled } from "@/components/Playground/utils";
import { displayErrorToast, displayToast } from "@/utils/toast";
import "@/routes/smart-table/column-highlight.css";

const isPlaygroundManagedReference = (reference: PlaygroundEvaluateSheetReference) =>
  reference.source_type === "playground_draft";

const isPlaygroundDraft = (reference: PlaygroundEvaluateSheetReference) =>
  isPlaygroundManagedReference(reference) && !reference.exported_at;

type DraftSetupState = "idle" | "creating" | "complete" | "failed" | "suppressed";

const Evaluate = () => {
  const { playground, queryScope } = useEvaluateScope();
  const queryClient = useQueryClient();
  const { confirm } = useConfirm();
  const { permissions, isCreateDisabled, isEditDisabled, isDeleteDisabled } = useRbac();
  const pendingReferenceUpdates = useIsMutating({
    mutationKey: playgroundEvaluateMutationKeys.sheetReferenceUpdate(
      queryScope.workspaceId,
      queryScope.sessionId,
    ),
  });
  const evaluateState = usePlaygroundEvaluateState(queryScope);
  const createDraft = useCreatePlaygroundEvaluateDraft(queryScope);
  const exportDraft = useExportPlaygroundEvaluateDraft(queryScope);
  const runReference = useRunEvaluateSheetReference(queryScope);
  const cancelRun = useCancelEvaluateRun(queryScope);
  const deleteReference = useDeleteEvaluateSheetReference(queryScope);
  const patchSettings = usePatchPlaygroundEvaluateSettings(queryScope);
  const promotePromptVersion = usePromotePlaygroundPromptVersion(queryScope);
  const references = useMemo(
    () => evaluateState.data?.sheet_references || [],
    [evaluateState.data?.sheet_references],
  );
  const associatedSheetGroups = useMemo(() => {
    const groups = new Map<
      string,
      { tableTitle: string; references: PlaygroundEvaluateSheetReference[] }
    >();

    references
      .filter(reference => reference.source_available && !isPlaygroundDraft(reference))
      .forEach(reference => {
        const group = groups.get(reference.smart_table_id);
        if (group) {
          group.references.push(reference);
          return;
        }
        groups.set(reference.smart_table_id, {
          tableTitle: reference.smart_table_title,
          references: [reference],
        });
      });

    return Array.from(groups, ([tableId, group]) => ({ tableId, ...group }));
  }, [references]);
  const sheetTabsContainerRef = useRef<HTMLDivElement>(null);
  const sheetTabRefs = useRef(new Map<string, HTMLDivElement>());
  const evaluateGridApiRef = useRef<GridApi<EvaluateAdHocRow> | null>(null);
  const previousReferenceIdsRef = useRef<string[] | null>(null);
  const draftCreationInFlightRef = useRef(false);
  const activationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const activationSequenceRef = useRef(0);
  const promotionPendingRef = useRef(false);
  const runPreparationPendingRef = useRef(false);
  const [localActiveReferenceId, setLocalActiveReferenceId] = useState<string | null>(null);
  const [dialogReference, setDialogReference] = useState<PlaygroundEvaluateSheetReference | null>();
  const [isReferenceDialogOpen, setIsReferenceDialogOpen] = useState(false);
  const [referenceToReplaceId, setReferenceToReplaceId] = useState<string | null>(null);
  const [editorReference, setEditorReference] = useState<PlaygroundEvaluateSheetReference | null>(
    null,
  );
  const [isSheetEditorOpen, setIsSheetEditorOpen] = useState(false);
  const [editorScorecardStepId, setEditorScorecardStepId] = useState<string | null>(null);
  const [evaluatorBreakdownView, setEvaluatorBreakdownView] = useState<
    Exclude<ScorecardViewState, { view: "overview" }>
  >({ view: "all-rows" });
  const [isEvaluatorBreakdownOpen, setIsEvaluatorBreakdownOpen] = useState(false);
  const [isPromotionDialogOpen, setIsPromotionDialogOpen] = useState(false);
  const [isPromotionPending, setIsPromotionPending] = useState(false);
  const [isPreparingRun, setIsPreparingRun] = useState(false);
  const [isSaveTableDialogOpen, setIsSaveTableDialogOpen] = useState(false);
  const [draftSetupState, setDraftSetupState] = useState<DraftSetupState>("idle");
  const [draftCreationError, setDraftCreationError] = useState<string | null>(null);
  const [promotedRunId, setPromotedRunId] = useState<string | null>(null);
  const persistedActiveReferenceId = evaluateState.data?.settings.active_sheet_reference_id ?? null;
  const activeReferenceId = localActiveReferenceId ?? persistedActiveReferenceId;
  const activeReference =
    references.find(reference => reference.id === activeReferenceId) || references[0] || null;
  const variableNames = getVariableNames(
    playground,
    Object.keys(activeReference?.input_variable_mappings ?? {}),
  );
  const variableSets = playground.input_variable_sets;
  const sourceRowLimit = Math.max(variableSets.length, 1);
  const sourceGrid = useEvaluateSheetGrid({
    userToken: queryScope.userToken,
    workspaceId: queryScope.workspaceId,
    smartTableId: activeReference?.smart_table_id ?? "",
    sheetId: activeReference?.sheet_id ?? "",
    limit: sourceRowLimit,
  });
  const currentEditorReference = editorReference
    ? references.find(reference => reference.id === editorReference.id) || editorReference
    : null;
  const shownEvaluateStateErrorRef = useRef<unknown>(null);

  useEffect(() => {
    if (!evaluateState.isError || shownEvaluateStateErrorRef.current === evaluateState.error) {
      return;
    }

    shownEvaluateStateErrorRef.current = evaluateState.error;
    displayErrorToast(
      evaluateState.error instanceof Error
        ? evaluateState.error.message
        : "Failed to load evaluation setup.",
    );
  }, [evaluateState.error, evaluateState.isError]);

  useEffect(() => {
    const referenceIds = references.map(reference => reference.id);
    const previousReferenceIds = previousReferenceIdsRef.current;
    previousReferenceIdsRef.current = referenceIds;

    if (!previousReferenceIds) {
      return;
    }

    const addedReferenceId = referenceIds.find(id => !previousReferenceIds.includes(id));
    const container = sheetTabsContainerRef.current;
    const addedTab = addedReferenceId ? sheetTabRefs.current.get(addedReferenceId) : null;
    if (!container || !addedTab) {
      return;
    }

    const containerBounds = container.getBoundingClientRect();
    const tabBounds = addedTab.getBoundingClientRect();
    const addButtonWidth = 40;
    const visibleRight = containerBounds.right - addButtonWidth;

    if (tabBounds.right > visibleRight) {
      container.scrollTo({
        left: container.scrollLeft + tabBounds.right - visibleRight,
        behavior: "smooth",
      });
    }
  }, [references]);

  const mutationRun = runReference.data?.run ?? null;
  const activeMutationRun =
    mutationRun && activeReference && mutationRun.sheet_reference_id === activeReference.id
      ? mutationRun
      : null;
  const activeRunId = activeMutationRun?.id || activeReference?.latest_run_summary?.id || null;
  const activeRunDetail = useEvaluateRunDetail({ ...queryScope, runId: activeRunId });
  const displayedRun =
    activeRunDetail.data?.run || activeMutationRun || activeReference?.latest_run_summary;
  const activeRunDetailRun = activeRunDetail.data?.run;
  const displayedRunRows =
    activeRunDetailRun && activeRunDetailRun.id === displayedRun?.id ? activeRunDetailRun.rows : [];
  const displayedRowCount =
    displayedRunRows.length ||
    Number(displayedRun?.summary?.row_count || displayedRun?.summary?.rows_run || 0);
  const displayedRowsPassed =
    displayedRunRows.filter(row => row.score_payload?.verdict === "pass").length ||
    Number(displayedRun?.summary?.rows_passed || 0);
  const displayedRowsCompleted =
    displayedRunRows.length > 0
      ? displayedRunRows.filter(row => row.status === "completed" || row.status === "failed").length
      : Number(displayedRun?.summary?.rows_run || 0);
  const displayedAggregateVerdict = String(displayedRun?.summary?.aggregate_verdict || "");
  const displayedRunError =
    displayedRun?.status === "failed" && typeof displayedRun.summary?.error === "string"
      ? displayedRun.summary.error
      : null;
  const isRunInProgress = displayedRun?.status === "queued" || displayedRun?.status === "running";
  const isDisplayedRunCancelled = displayedRun?.status === "cancelled";
  const canEdit = !playground.readonly;
  const canCreateDraft =
    canEdit &&
    permissions !== undefined &&
    !isCreateDisabled(EntityType.SMART_TABLE) &&
    !isEditDisabled(EntityType.SMART_TABLE);
  const { selectedTemplateVersion } = playground;
  const selectedPromptRegistry = usePromptRegistry(
    queryScope.userToken,
    selectedTemplateVersion?.prompt_id,
    false,
  );
  const configurationDisabled =
    !canEdit || isPreparingRun || isRunInProgress || runReference.isPending;
  const isCreatingDraft = draftSetupState === "creating" || createDraft.isPending;
  const isPreparingDraft =
    isCreatingDraft ||
    (draftSetupState === "idle" && (permissions === undefined || canCreateDraft));
  const handleCreateDraft = useCallback(
    async (createNew = false) => {
      if (draftCreationInFlightRef.current || createDraft.isPending) {
        return;
      }

      draftCreationInFlightRef.current = true;
      setDraftSetupState("creating");
      setDraftCreationError(null);
      try {
        await playground.waitForSessionSaveCompletion();
        const response = await createDraft.mutateAsync({
          ...(selectedTemplateVersion
            ? {
                prompt_registry_id: selectedTemplateVersion.prompt_id,
                prompt_version_number: selectedTemplateVersion.number,
              }
            : {}),
          ...(createNew ? { create_new: true } : {}),
        });
        setLocalActiveReferenceId(response.sheet_reference.id);
        setDraftSetupState("complete");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Couldn’t create the evaluation draft. Your prompt and input sets are unchanged.";
        if (createNew) {
          displayErrorToast(message);
          setDraftSetupState("complete");
        } else {
          setDraftCreationError(message);
          setDraftSetupState("failed");
        }
      } finally {
        draftCreationInFlightRef.current = false;
      }
    },
    [createDraft, playground, selectedTemplateVersion],
  );

  const activateReference = useCallback(
    (referenceId: string): Promise<void> => {
      const sequence = activationSequenceRef.current + 1;
      activationSequenceRef.current = sequence;
      setLocalActiveReferenceId(referenceId);

      const activationRequest = activationQueueRef.current
        .catch(() => undefined)
        .then(() =>
          patchSettings
            .mutateAsync({ active_sheet_reference_id: referenceId })
            .then(() => undefined),
        );
      activationQueueRef.current = activationRequest.catch(() => undefined);

      return activationRequest.then(
        () => {
          if (activationSequenceRef.current === sequence) {
            setLocalActiveReferenceId(null);
          }
        },
        error => {
          if (activationSequenceRef.current === sequence) {
            setLocalActiveReferenceId(null);
          }
          throw error;
        },
      );
    },
    [patchSettings],
  );
  const handleReferenceCreated = useCallback(
    async (reference: PlaygroundEvaluateSheetReference) => {
      try {
        await activateReference(reference.id);
        if (referenceToReplaceId && referenceToReplaceId !== reference.id) {
          await deleteReference.mutateAsync(referenceToReplaceId);
        }
      } catch {
        // Mutation hooks surface the actionable error. Keep both references on partial replacement.
      } finally {
        setReferenceToReplaceId(null);
      }
    },
    [activateReference, deleteReference, referenceToReplaceId],
  );
  const handleRemoveReference = async (reference: PlaygroundEvaluateSheetReference) => {
    if (isPlaygroundDraft(reference)) {
      const confirmed = await confirm({
        title: "Discard Draft Sheet?",
        message:
          "This removes this draft sheet and its Playground history. This action cannot be undone.",
        confirmText: "Discard Sheet",
        cancelText: "Keep Sheet",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
      });
      if (!confirmed) {
        return;
      }
    }

    const isRemovingLastReference = references.length === 1;
    const previousSetupState = draftSetupState;
    if (isRemovingLastReference) {
      setDraftSetupState("suppressed");
      setDraftCreationError(null);
    }

    deleteReference.mutate(reference.id, {
      onSuccess: () => {
        if (localActiveReferenceId === reference.id) {
          setLocalActiveReferenceId(null);
        }
      },
      onError: () => {
        if (isRemovingLastReference) {
          setDraftSetupState(previousSetupState);
        }
      },
    });
  };
  const handleConfigureInputSet = useCallback(
    (setId?: string) => {
      let targetSetId = setId ?? playground.input_variable_sets[0]?.id;
      if (!targetSetId) {
        const textColumnIds = new Set(
          activeReference?.columns.filter(isEvaluateInputColumn).map(column => column.id) ?? [],
        );
        const mappedInputVariableKeys = Object.entries(
          activeReference?.input_variable_mappings ?? {},
        ).flatMap(([variable, mapping]) =>
          textColumnIds.has(mapping.column_id) ? [variable] : [],
        );
        targetSetId = playground.addEmptyInputVariableSet(mappedInputVariableKeys);
      }
      playground.setActiveInputVariableSetId(targetSetId);
      playground.startEditingVariableSet(targetSetId);
    },
    [activeReference, playground],
  );

  const refreshEditorData = () => {
    if (!currentEditorReference) {
      return;
    }

    void queryClient.invalidateQueries({
      queryKey: playgroundEvaluateQueryKeys.state(queryScope.workspaceId, queryScope.sessionId),
    });
    void queryClient.invalidateQueries({
      queryKey: playgroundEvaluateQueryKeys.sheetGrid(
        queryScope.workspaceId,
        currentEditorReference.smart_table_id,
        currentEditorReference.sheet_id,
        sourceRowLimit,
      ),
    });
  };

  const handleSheetEditorOpenChange = (open: boolean) => {
    setIsSheetEditorOpen(open);
    if (!open) {
      refreshEditorData();
      setEditorScorecardStepId(null);
    }
  };

  const openEvaluatorEditor = (stepId: string) => {
    if (!activeReference || configurationDisabled) {
      return;
    }
    setEditorReference(activeReference);
    setEditorScorecardStepId(stepId);
    setIsSheetEditorOpen(true);
  };

  useEffect(() => {
    draftCreationInFlightRef.current = false;
    setDraftSetupState("idle");
    setDraftCreationError(null);
  }, [queryScope.sessionId]);

  useEffect(() => {
    if (
      evaluateState.isLoading ||
      evaluateState.isError ||
      !canEdit ||
      permissions === undefined ||
      references.length > 0 ||
      draftSetupState !== "idle"
    ) {
      return;
    }

    if (!canCreateDraft) {
      setDraftSetupState("suppressed");
      return;
    }

    void handleCreateDraft();
  }, [
    canCreateDraft,
    canEdit,
    draftSetupState,
    evaluateState.isError,
    evaluateState.isLoading,
    handleCreateDraft,
    permissions,
    references.length,
  ]);

  if (evaluateState.isLoading) {
    return (
      <div
        className="space-y-3 p-4"
        data-playground-outputs-auto-fit-content
        data-playground-outputs-auto-fit-ready="false"
      >
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (evaluateState.isError && !evaluateState.data) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <div>
          <p className="text-sm font-medium text-gray-800">Evaluation setup couldn’t be loaded</p>
          <p className="mt-1 text-xs text-gray-500">Check your connection and try again.</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={evaluateState.isFetching}
          onClick={() => void evaluateState.refetch()}
        >
          <RefreshCwIcon className={cn("h-4 w-4", evaluateState.isFetching && "animate-spin")} />
          Try Again
        </Button>
      </div>
    );
  }

  if (!activeReference) {
    return (
      <>
        <EmptyEvaluateState
          canCreateDraft={canCreateDraft}
          canEdit={canEdit}
          createError={draftCreationError}
          isPreparingDraft={isPreparingDraft}
          onCreateDraft={() => void handleCreateDraft()}
          onReferenceSheet={() => {
            setDialogReference(null);
            setIsReferenceDialogOpen(true);
          }}
        />
        <ReferenceSheetDialog
          open={isReferenceDialogOpen}
          onOpenChange={setIsReferenceDialogOpen}
          existingReferenceCount={references.length}
          onReferenceCreated={handleReferenceCreated}
        />
      </>
    );
  }

  const activeReferenceIsDraft = isPlaygroundDraft(activeReference);
  const evaluatorCount = activeReference.active_scorecard?.steps.length ?? 0;
  const canReplaceDefaultDraft =
    references.length === 1 &&
    activeReferenceIsDraft &&
    evaluatorCount === 0 &&
    !activeReference.latest_run_summary &&
    !isDeleteDisabled(EntityType.SMART_TABLE);
  const waitForReferenceUpdates = () => {
    const mutationKey = playgroundEvaluateMutationKeys.sheetReferenceUpdate(
      queryScope.workspaceId,
      queryScope.sessionId,
    );
    return new Promise<void>(resolve => {
      let unsubscribe: () => void = () => undefined;
      const resolveWhenSettled = () => {
        if (queryClient.isMutating({ mutationKey }) === 0) {
          unsubscribe();
          resolve();
        }
      };

      unsubscribe = queryClient.getMutationCache().subscribe(resolveWhenSettled);
      resolveWhenSettled();
    });
  };
  const saveDraftAsTable = async (
    name: string,
    folderId: number | null,
    prompt: PlaygroundEvaluateExportPrompt,
  ) => {
    if (!activeReferenceIsDraft) {
      throw new Error("This evaluation draft has already been saved.");
    }
    if (exportDraft.isPending) {
      throw new Error("This evaluation draft is already being saved.");
    }

    await Promise.all([playground.waitForSessionSaveCompletion(), waitForReferenceUpdates()]);
    const response = await exportDraft.mutateAsync({
      referenceId: activeReference.id,
      folder_id: folderId,
      title: name,
      prompt,
    });
    if (response.prompt_version) {
      playground.setSelectedTemplateVersion(response.prompt_version);
      if (
        response.prompt_action === "create_version" ||
        response.prompt_action === "create_prompt"
      ) {
        playground.reassignInitialPayload();
      }
    }
    let successMessage = "Table created";
    if (response.prompt_action === "create_prompt") {
      successMessage = `Table and ${response.prompt_name || "Prompt Template"} created`;
    } else if (response.prompt_action === "create_version") {
      successMessage = `Table created and ${response.prompt_name || "Prompt Template"} v${response.prompt_version?.number} saved`;
    } else if (response.prompt_action === "link_existing") {
      successMessage = `Table linked to ${response.prompt_name || "Prompt Template"} v${response.prompt_version?.number}`;
    }
    displayToast(successMessage, ToastType.success, {
      action: {
        label: "Open Table",
        onClick: () =>
          window.open(
            `/workspace/${playground.workspace_id}/smart-tables/${activeReference.smart_table_id}?sheet=${activeReference.sheet_id}`,
            "_blank",
            "noopener,noreferrer",
          ),
      },
    });
  };

  const mappableColumnIds = new Set(
    activeReference.columns.filter(isEvaluateInputColumn).map(column => column.id),
  );

  const mappedColumnIds = variableNames
    .map(variable => activeReference.input_variable_mappings[variable]?.column_id)
    .filter((columnId): columnId is string => Boolean(columnId && mappableColumnIds.has(columnId)));
  const hasDuplicateMappings = new Set(mappedColumnIds).size !== mappedColumnIds.length;
  const effectiveInputVariableSets = getEffectiveInputVariableSets({
    variableSets,
    inputMappings: activeReference.input_variable_mappings,
    sourceRows: sourceGrid.data?.rows ?? [],
    requiredVariableNames: variableNames,
  });
  const requiredInputVariableNames = new Set([
    ...playground.detectedInputVariables,
    ...activeReference.mapping_status.mapped_variables,
    ...activeReference.mapping_status.missing_variables,
    ...Object.keys(activeReference.input_variable_mappings),
  ]);
  const missingInputValues = effectiveInputVariableSets.flatMap((inputSet, setIndex) =>
    Array.from(requiredInputVariableNames).flatMap(variableName =>
      getVariableValueIsFilled(inputSet[variableName]) ? [] : [{ setIndex, variableName }],
    ),
  );
  const unresolvedVariableNames = Array.from(
    new Set(missingInputValues.map(({ variableName }) => variableName)),
  );
  const missingInputSetCount = new Set(missingInputValues.map(({ setIndex }) => setIndex)).size;
  const sourceRowCount = sourceGrid.data?.row_count ?? sourceGrid.data?.rows.length ?? 0;
  const displayedRunDetail = activeRunDetailRun ?? activeMutationRun;
  const displayedRunStaleState = getEvaluateRunStaleState({
    run: displayedRunDetail,
    currentInputVariableSets: effectiveInputVariableSets,
    inputMappings: activeReference.input_variable_mappings,
    templateOverrideColumnId: activeReference.template_override?.column_id,
  });
  const isDisplayedRunStale = displayedRunStaleState.isRunStale;
  const isDisplayedScoreStale = displayedRunStaleState.staleStepIds.length > 0;
  const rowLimit = variableSets.length;
  const hasPromptTemplateColumns = activeReference.columns.some(
    column => column.type === PROMPT_TEMPLATE_COLUMN_TYPE,
  );
  let setupIssue: string | null = null;
  if (!activeReference.source_available) {
    setupIssue = "The referenced sheet is unavailable";
  } else if (variableSets.length === 0) {
    setupIssue = "Add an input set before evaluating";
  } else if (sourceGrid.isLoading) {
    setupIssue = "Loading source sheet defaults";
  } else if (sourceGrid.isError) {
    setupIssue = "Source sheet defaults couldn’t be loaded";
  } else if (sourceGrid.isFetching) {
    setupIssue = "Refreshing source sheet defaults";
  } else if (pendingReferenceUpdates > 0) {
    setupIssue = "Saving input assignments";
  } else if (hasDuplicateMappings) {
    setupIssue = "Map each input variable to a different text column";
  } else if (variableSets.length > sourceRowCount) {
    setupIssue = `This sheet has ${sourceRowCount} ${sourceRowCount === 1 ? "row" : "rows"} for ${variableSets.length} input sets`;
  } else if (missingInputValues.length > 0) {
    setupIssue = `${missingInputValues.length} missing input ${
      missingInputValues.length === 1 ? "value" : "values"
    } across ${missingInputSetCount} input ${missingInputSetCount === 1 ? "set" : "sets"}`;
  } else if (!activeReference.template_override?.column_id) {
    setupIssue = "Choose the Prompt Template column to evaluate";
  } else if (activeReferenceIsDraft && evaluatorCount === 0) {
    setupIssue = "Add an evaluator before running this draft";
  }
  const ready = setupIssue === null;
  const canPromotePrompt = Boolean(
    canEdit &&
      !isEditDisabled(EntityType.SMART_TABLE) &&
      !isEditDisabled(EntityType.PROMPT) &&
      !activeReferenceIsDraft &&
      displayedRun?.id &&
      displayedRun.status === "completed" &&
      displayedRun.score_delta !== null &&
      displayedRun.score_delta > 0 &&
      displayedRun.id !== promotedRunId &&
      !displayedRun.summary?.promoted_prompt_version_id &&
      !isDisplayedRunStale &&
      playground.selectedTemplateVersion,
  );
  const promoteCurrentPrompt = async () => {
    const currentVersion = playground.selectedTemplateVersion;
    if (!currentVersion || !displayedRun?.id || promotionPendingRef.current) {
      return;
    }

    promotionPendingRef.current = true;
    setIsPromotionPending(true);
    try {
      await playground.waitForSessionSaveCompletion();
      const response = await promotePromptVersion.mutateAsync({
        referenceId: activeReference.id,
        run_id: displayedRun.id,
        prompt_id: currentVersion.prompt_id,
        parent_version_id: currentVersion.id,
        smartTableId: activeReference.smart_table_id,
        sheetId: activeReference.sheet_id,
      });
      playground.setSelectedTemplateVersion(response.prompt_version);
      playground.reassignInitialPayload();
      setPromotedRunId(displayedRun.id);
      setIsPromotionDialogOpen(false);
      displayToast(
        `${activeReference.smart_table_title} updated to Prompt Template v${response.prompt_version.number}`,
        ToastType.success,
        {
          action: {
            label: "Open Table",
            onClick: () =>
              window.open(
                `/workspace/${playground.workspace_id}/smart-tables/${activeReference.smart_table_id}?sheet=${activeReference.sheet_id}`,
                "_blank",
                "noopener,noreferrer",
              ),
          },
        },
      );
    } catch (error) {
      displayErrorToast(
        error instanceof Error ? error.message : "Failed to update the Table prompt.",
      );
    } finally {
      promotionPendingRef.current = false;
      setIsPromotionPending(false);
    }
  };
  const runEvaluation = async () => {
    if (runPreparationPendingRef.current) {
      return;
    }

    runPreparationPendingRef.current = true;
    setIsPreparingRun(true);
    evaluateGridApiRef.current?.stopEditing();

    try {
      await Promise.resolve();
      await Promise.all([playground.waitForSessionSaveCompletion(), waitForReferenceUpdates()]);

      const latestEvaluateState = queryClient.getQueryData<PlaygroundEvaluateStateResponse>(
        playgroundEvaluateQueryKeys.state(queryScope.workspaceId, queryScope.sessionId),
      );
      const latestReference =
        latestEvaluateState?.sheet_references.find(
          reference => reference.id === activeReference.id,
        ) ?? activeReference;
      const latestVariableNames = getVariableNames(
        playground,
        Object.keys(latestReference.input_variable_mappings),
      );
      const latestInputVariableSets = getEffectiveInputVariableSets({
        variableSets: playground.input_variable_sets,
        inputMappings: latestReference.input_variable_mappings,
        sourceRows: sourceGrid.data?.rows ?? [],
        requiredVariableNames: latestVariableNames,
      });
      if (latestInputVariableSets.length === 0) {
        return;
      }

      await runReference.mutateAsync({
        referenceId: latestReference.id,
        row_scope: { mode: "first_n", limit: latestInputVariableSets.length },
        input_variable_sets: latestInputVariableSets,
      });
    } catch {
      // Mutation errors are surfaced by the query hooks.
    } finally {
      runPreparationPendingRef.current = false;
      setIsPreparingRun(false);
    }
  };
  const stopEvaluation = () => {
    if (!displayedRun?.id || !isRunInProgress || cancelRun.isPending) {
      return;
    }
    cancelRun.mutate(displayedRun.id);
  };
  let displayedVerdictLabel: ReactNode = "Complete";
  let displayedVerdictClassName = verdictClassName(displayedAggregateVerdict || "pass");
  if (isDisplayedRunCancelled) {
    displayedVerdictLabel = "Stopped";
    displayedVerdictClassName = "border-gray-200 bg-gray-50 text-gray-600";
  } else if (displayedRun?.scoring_type === "scorecard") {
    displayedVerdictLabel = (
      <BasicTooltip
        content="These scores are only for this Playground run. They do not update or affect scores in the source sheet."
        side="top"
      >
        <span
          className="inline-flex cursor-help items-center text-gray-400 transition-colors hover:text-gray-600"
          aria-label="About Playground evaluation scores"
          role="img"
        >
          <HelpCircleIcon className="h-4 w-4" />
        </span>
      </BasicTooltip>
    );
    displayedVerdictClassName = "!border-0 !bg-transparent !p-0";
  }

  const sourceDescription = activeReferenceIsDraft ? "Only in this Playground" : "Referenced Table";
  let sourceEditorLabel = "Edit Table";
  if (activeReferenceIsDraft) {
    sourceEditorLabel = evaluatorCount === 0 ? "Add Evaluator" : "Edit Draft";
  }
  const draftSheetCount = references.filter(isPlaygroundDraft).length;
  const defaultPromptName =
    playground.sessionName?.trim() ||
    (activeReference.smart_table_title === "Ad hoc evaluation"
      ? "Playground Prompt"
      : activeReference.smart_table_title.replace(/\s+evaluation$/i, "").trim());

  return (
    <div
      className="flex w-full min-w-0 max-w-full shrink-0 flex-col gap-4 px-4 pb-3 pt-2"
      data-playground-outputs-auto-fit-content
      data-playground-outputs-auto-fit-ready={String(!sourceGrid.isLoading)}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-gray-200 pb-2">
        <div
          ref={sheetTabsContainerRef}
          className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto"
          aria-label="Evaluation sheets"
        >
          {references.map(reference => {
            const selected = reference.id === activeReference.id;
            const referenceIsManaged = isPlaygroundManagedReference(reference);
            const referenceIsDraft = isPlaygroundDraft(reference);
            const referenceRunInProgress =
              reference.latest_run_summary?.status === "queued" ||
              reference.latest_run_summary?.status === "running";
            const canReplaceReference =
              referenceIsDraft &&
              (reference.active_scorecard?.steps.length ?? 0) === 0 &&
              !reference.latest_run_summary &&
              !isDeleteDisabled(EntityType.SMART_TABLE);
            const discardDisabled =
              deleteReference.isPending ||
              (referenceIsDraft && isDeleteDisabled(EntityType.SMART_TABLE)) ||
              (referenceIsDraft &&
                (referenceRunInProgress ||
                  (selected && (isPreparingRun || runReference.isPending || isRunInProgress))));
            const referenceLabel = referenceIsDraft
              ? reference.sheet_title
              : `${reference.smart_table_title} / ${reference.sheet_title}`;
            return (
              <div
                key={reference.id}
                ref={node => {
                  if (node) {
                    sheetTabRefs.current.set(reference.id, node);
                  } else {
                    sheetTabRefs.current.delete(reference.id);
                  }
                }}
                className={cn(
                  "group flex h-8 max-w-[280px] shrink-0 items-center rounded-md text-sm transition-colors",
                  selected
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800",
                )}
              >
                <button
                  type="button"
                  onClick={() => void activateReference(reference.id).catch(() => undefined)}
                  className="flex h-full min-w-0 items-center gap-2 pl-2.5 pr-1"
                >
                  <Table2Icon className="h-3.5 w-3.5 shrink-0 text-cyan-600" />
                  <span className="flex min-w-0 items-start gap-0.5">
                    <OverflowDisplayTooltip
                      content={referenceLabel}
                      className={cn("min-w-0 font-medium", referenceIsDraft && "text-cyan-600")}
                    >
                      {referenceLabel}
                    </OverflowDisplayTooltip>
                    {referenceIsDraft && (
                      <span className="shrink-0 rounded bg-cyan-50 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-cyan-700">
                        Draft
                      </span>
                    )}
                  </span>
                  {reference.latest_run_summary?.aggregate_score !== null && (
                    <span className="shrink-0 text-xs text-gray-400">
                      {scoreLabel(reference.latest_run_summary?.aggregate_score)}
                    </span>
                  )}
                </button>
                {canEdit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 transition-opacity hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300",
                          selected
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100",
                        )}
                        aria-label={`Actions for ${reference.sheet_title}`}
                      >
                        <MoreHorizontalIcon className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 rounded-lg p-1">
                      {!referenceIsDraft && (
                        <DropdownMenuItem
                          onSelect={() =>
                            window.open(
                              `/workspace/${playground.workspace_id}/smart-tables/${reference.smart_table_id}?sheet=${reference.sheet_id}`,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                          className="h-9 cursor-pointer rounded-md text-gray-700"
                        >
                          <ExternalLinkIcon className="h-4 w-4 text-gray-400" />
                          Open in New Tab
                        </DropdownMenuItem>
                      )}

                      {canReplaceReference && (
                        <DropdownMenuItem
                          onSelect={() => {
                            setReferenceToReplaceId(reference.id);
                            setDialogReference(null);
                            setIsReferenceDialogOpen(true);
                          }}
                          className="h-9 cursor-pointer rounded-md text-gray-700"
                        >
                          <RefreshCwIcon className="h-4 w-4 text-gray-400" />
                          Replace This Sheet…
                        </DropdownMenuItem>
                      )}
                      {!referenceIsManaged && (
                        <DropdownMenuItem
                          onSelect={() => {
                            setDialogReference(reference);
                            setIsReferenceDialogOpen(true);
                          }}
                          className="h-9 cursor-pointer rounded-md text-gray-700"
                        >
                          <RefreshCwIcon className="h-4 w-4 text-gray-400" />
                          Change Sheet
                        </DropdownMenuItem>
                      )}
                      {(!referenceIsDraft || canReplaceReference) && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        disabled={discardDisabled}
                        onSelect={() => void handleRemoveReference(reference)}
                        className="h-9 cursor-pointer rounded-md text-red-600 focus:text-red-600"
                      >
                        <Trash2Icon className="h-4 w-4" />
                        {referenceIsDraft ? "Discard Draft" : "Remove Sheet"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
          {canReplaceDefaultDraft && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setReferenceToReplaceId(activeReference.id);
                setDialogReference(null);
                setIsReferenceDialogOpen(true);
              }}
              className="ml-1 h-8 shrink-0 gap-1.5 px-2 text-xs font-medium text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-800"
            >
              <Table2Icon className="h-3.5 w-3.5" />
              Use Existing Table
            </Button>
          )}
          {canEdit && (
            <div className="sticky right-0 z-10 ml-1 flex shrink-0 pl-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={isCreatingDraft}
                    className="h-8 shrink-0 gap-1.5 px-2 text-xs font-medium text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-800"
                  >
                    {isCreatingDraft ? (
                      <RefreshCwIcon className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <PlusIcon className="h-3.5 w-3.5" />
                    )}
                    Add Sheet
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 rounded-lg p-1">
                  {associatedSheetGroups.length > 0 && (
                    <>
                      <DropdownMenuLabel className="px-2 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                        Associated Sheets
                      </DropdownMenuLabel>
                      {associatedSheetGroups.map(group => {
                        const [onlyReference] = group.references;
                        if (group.references.length === 1 && onlyReference) {
                          return (
                            <DropdownMenuItem
                              key={onlyReference.id}
                              onSelect={() =>
                                void activateReference(onlyReference.id).catch(() => undefined)
                              }
                              className={cn(
                                "min-h-10 cursor-pointer rounded-md px-2 py-1.5 text-gray-700",
                                onlyReference.id === activeReference.id && "bg-gray-100",
                              )}
                            >
                              <EntityIcon
                                type={EntityType.SMART_TABLE}
                                size="xs"
                                className="shrink-0"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-gray-800">
                                  {onlyReference.sheet_title}
                                </span>
                                <span className="block truncate text-[11px] leading-4 text-gray-400">
                                  {group.tableTitle}
                                </span>
                              </span>
                              {onlyReference.id === activeReference.id && (
                                <CheckIcon className="h-3.5 w-3.5 text-cyan-600" />
                              )}
                            </DropdownMenuItem>
                          );
                        }

                        return (
                          <DropdownMenuSub key={group.tableId}>
                            <DropdownMenuSubTrigger className="min-h-10 cursor-pointer rounded-md px-2 py-1.5 text-gray-700">
                              <EntityIcon
                                type={EntityType.SMART_TABLE}
                                size="xs"
                                className="shrink-0"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-gray-800">
                                  {group.tableTitle}
                                </span>
                                <span className="block text-[11px] leading-4 text-gray-400">
                                  {group.references.length} associated sheets
                                </span>
                              </span>
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-64 rounded-lg p-1">
                              <DropdownMenuLabel className="truncate px-2 pb-1 pt-1.5 text-[11px] font-medium text-gray-400">
                                {group.tableTitle}
                              </DropdownMenuLabel>
                              {group.references.map(reference => (
                                <DropdownMenuItem
                                  key={reference.id}
                                  onSelect={() =>
                                    void activateReference(reference.id).catch(() => undefined)
                                  }
                                  className={cn(
                                    "h-9 cursor-pointer rounded-md text-gray-700",
                                    reference.id === activeReference.id && "bg-gray-100",
                                  )}
                                >
                                  <Table2Icon className="h-3.5 w-3.5 text-cyan-600" />
                                  <span className="min-w-0 flex-1 truncate">
                                    {reference.sheet_title}
                                  </span>
                                  {reference.id === activeReference.id && (
                                    <CheckIcon className="h-3.5 w-3.5 text-cyan-600" />
                                  )}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        );
                      })}
                      <DropdownMenuSeparator />
                    </>
                  )}

                  <DropdownMenuItem
                    disabled={!canCreateDraft || isCreatingDraft}
                    onSelect={() => void handleCreateDraft(true)}
                    className="h-9 cursor-pointer rounded-md text-gray-700"
                  >
                    <PlusIcon className="h-4 w-4 text-cyan-600" />
                    Create Draft Sheet
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      setReferenceToReplaceId(null);
                      setDialogReference(null);
                      setIsReferenceDialogOpen(true);
                    }}
                    className="h-9 cursor-pointer rounded-md text-gray-700"
                  >
                    <EntityIcon type={EntityType.SMART_TABLE} size="xs" className="shrink-0" />
                    Choose Another Table…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <ReferenceSheetDialog
        open={isReferenceDialogOpen}
        onOpenChange={open => {
          setIsReferenceDialogOpen(open);
          if (!open) {
            setReferenceToReplaceId(null);
          }
        }}
        existingReferenceCount={references.length}
        reference={dialogReference ?? undefined}
        onReferenceCreated={handleReferenceCreated}
      />

      <div className="flex min-h-12 shrink-0 items-center justify-between gap-3 border-b border-gray-100 pb-3">
        <div className="flex min-w-0 items-start gap-2">
          <EntityIcon type={EntityType.SMART_TABLE} size="sm" className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div
              className={cn(
                "truncate text-sm font-medium",
                activeReferenceIsDraft ? "text-cyan-700" : "text-gray-800",
              )}
            >
              {activeReferenceIsDraft
                ? activeReference.sheet_title
                : `${activeReference.smart_table_title} / ${activeReference.sheet_title}`}
            </div>
            <p className="mt-0.5 truncate text-xs text-gray-400">{sourceDescription}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={activeReferenceIsDraft && evaluatorCount === 0 ? "default" : "ghost"}
            disabled={configurationDisabled}
            onMouseEnter={preloadSmartTablePage}
            onFocus={preloadSmartTablePage}
            onClick={() => {
              setEditorReference(activeReference);
              setEditorScorecardStepId(null);
              setIsSheetEditorOpen(true);
            }}
            className={cn(
              "h-8 shrink-0 gap-1.5 px-2.5 text-xs shadow-none",
              (!activeReferenceIsDraft || evaluatorCount > 0) &&
                "text-gray-500 hover:bg-gray-100 hover:text-gray-800",
            )}
          >
            {activeReferenceIsDraft && evaluatorCount === 0 ? (
              <PlusIcon className="h-3.5 w-3.5" />
            ) : (
              <PencilIcon className="h-3.5 w-3.5" />
            )}
            {sourceEditorLabel}
          </Button>
          {activeReferenceIsDraft && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={
                  configurationDisabled ||
                  !canCreateDraft ||
                  exportDraft.isPending ||
                  pendingReferenceUpdates > 0
                }
                onClick={() => setIsSaveTableDialogOpen(true)}
                className="h-8 shrink-0 gap-1.5 bg-white px-2.5 text-xs text-gray-600 shadow-none hover:text-gray-900"
              >
                <EntityIcon type={EntityType.SMART_TABLE} size="xs" />
                Create Table
              </Button>
              <CreateTableDialog
                open={isSaveTableDialogOpen}
                onOpenChange={setIsSaveTableDialogOpen}
                draftSheetCount={draftSheetCount}
                defaultTableName={activeReference.smart_table_title}
                defaultPromptName={defaultPromptName}
                currentFolderId={selectedTemplateVersion?.folder_id ?? null}
                selectedTemplateVersion={selectedTemplateVersion}
                selectedPromptName={selectedPromptRegistry.data?.prompt_name}
                canCreatePrompt={!isCreateDisabled(EntityType.PROMPT)}
                canEditPrompt={!isEditDisabled(EntityType.PROMPT)}
                onCreate={saveDraftAsTable}
              />
            </>
          )}
        </div>
      </div>

      {activeReferenceIsDraft && exportDraft.isError && (
        <div
          className="flex items-center justify-between gap-3 rounded-md bg-red-50 px-3 py-2"
          role="alert"
          aria-live="polite"
        >
          <p className="text-xs text-red-700">
            {exportDraft.error instanceof Error
              ? exportDraft.error.message
              : "Couldn’t create this Table. The draft is still available here."}
          </p>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setIsSaveTableDialogOpen(true)}
            className="h-7 shrink-0 px-2 text-xs text-red-700 hover:bg-red-100 hover:text-red-800"
          >
            Try Again
          </Button>
        </div>
      )}

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <EvaluateInputConfiguration
            variableSets={variableSets}
            variableNames={variableNames}
            reference={activeReference}
            mappedColumnIds={mappedColumnIds}
            hasDuplicateMappings={hasDuplicateMappings}
            unresolvedVariableNames={unresolvedVariableNames}
            sourceRowCount={sourceRowCount}
            disabled={configurationDisabled}
            onConfigureInputSet={handleConfigureInputSet}
          />
          <TemplateOverrideSelect reference={activeReference} disabled={configurationDisabled} />
        </div>
        <div className="shrink-0">
          <RunButton
            run={runEvaluation}
            cancel={displayedRun?.id && isRunInProgress ? stopEvaluation : undefined}
            disabled={!ready || !canEdit}
            disabledTooltip={
              !canEdit ? "This Playground is read-only" : setupIssue || "Evaluation is ready"
            }
            isRunning={
              isPreparingRun || runReference.isPending || isRunInProgress || cancelRun.isPending
            }
            buttonText={{
              idle: isDisplayedRunStale
                ? "Evaluate again"
                : `Evaluate ${rowLimit} input ${rowLimit === 1 ? "set" : "sets"}`,
              running: "Evaluating...",
            }}
            tooltipText={{
              idle: isDisplayedRunStale
                ? "Refresh scores with the current input sets and mappings"
                : `Evaluate ${rowLimit} input ${rowLimit === 1 ? "set" : "sets"}`,
              running: "Evaluation in progress...",
            }}
            tooltipSide="bottom"
          />
        </div>
      </div>

      {!hasPromptTemplateColumns && <MissingPromptTemplateNotice />}

      <div className="relative min-w-0 max-w-full overflow-hidden rounded-xl border border-gray-200 bg-white">
        <EvaluateAdHocSheet
          displayedRun={activeRunDetailRun || displayedRun}
          isRunDetailLoading={Boolean(
            displayedRun?.id && !activeRunDetailRun && activeRunDetail.isLoading,
          )}
          reference={activeReference}
          variableSets={variableSets}
          sourceColumns={sourceGrid.data?.columns ?? []}
          sourceRows={sourceGrid.data?.rows ?? []}
          sourceRowCount={sourceRowCount}
          isSourceLoading={sourceGrid.isLoading}
          isPromptOutputStale={displayedRunStaleState.isPromptOutputStale}
          canEdit={!configurationDisabled}
          gridApiRef={evaluateGridApiRef}
          onConfigureInputSet={handleConfigureInputSet}
        />
      </div>

      {displayedRun && (
        <section className="shrink-0 space-y-4">
          {displayedRun.status === "running" || displayedRun.status === "queued" ? (
            <ScorecardHeaderCard
              state={{
                kind: "running",
                score: `${scoreLabel(displayedRun.aggregate_score)} score`,
                statusLabel: "Evaluating",
                progress: {
                  scoredRows: displayedRowsCompleted,
                  totalRows: displayedRowCount || rowLimit,
                },
                metaLabel: `${displayedRowsCompleted} of ${displayedRowCount || rowLimit} input ${(displayedRowCount || rowLimit) === 1 ? "set" : "sets"} evaluated`,
              }}
              onOpenAllRows={() => {
                setEvaluatorBreakdownView({ view: "all-rows" });
                setIsEvaluatorBreakdownOpen(true);
              }}
            />
          ) : (
            <ScorecardHeaderCard
              state={{
                kind: "score",
                score: `${scoreLabel(displayedRun.aggregate_score)}`,
                drift: isDisplayedRunCancelled ? null : displayedRun.score_delta,
                verdictLabel: displayedVerdictLabel,
                verdictClassName: displayedVerdictClassName,
                metaLabel: isDisplayedRunCancelled
                  ? `Stopped after ${displayedRowsCompleted} of ${displayedRowCount || rowLimit} input ${(displayedRowCount || rowLimit) === 1 ? "set" : "sets"}`
                  : `${displayedRowsPassed} of ${displayedRowCount || rowLimit} input ${(displayedRowCount || rowLimit) === 1 ? "set" : "sets"} passed`,
                isStale: !isDisplayedRunCancelled && isDisplayedScoreStale,
              }}
              onOpenAllRows={() => {
                setEvaluatorBreakdownView({ view: "all-rows" });
                setIsEvaluatorBreakdownOpen(true);
              }}
            />
          )}

          {canPromotePrompt && displayedRun.score_delta !== null && (
            <UsePromptInTableAction
              isPending={isPromotionPending}
              scoreDelta={displayedRun.score_delta}
              smartTableTitle={activeReference.smart_table_title}
              onSelect={() => setIsPromotionDialogOpen(true)}
            />
          )}

          <CompactEvaluatorCards
            runSummary={displayedRun.summary}
            scorecardSnapshot={displayedRun.scorecard_snapshot}
            columns={activeReference.columns}
            rows={displayedRunRows}
            staleStepIds={displayedRunStaleState.staleStepIds}
            editableStepIds={
              configurationDisabled
                ? []
                : (activeReference.active_scorecard?.steps.map(step => step.id) ?? [])
            }
            onOpenCriterion={stepId => {
              setEvaluatorBreakdownView({ view: "criterion", stepId });
              setIsEvaluatorBreakdownOpen(true);
            }}
            onEditCriterion={openEvaluatorEditor}
          />

          {displayedRunError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {displayedRunError}
            </div>
          )}
        </section>
      )}

      <ImportedSheetEditor
        open={isSheetEditorOpen}
        reference={currentEditorReference}
        onOpenChange={handleSheetEditorOpenChange}
        onChangesSettled={refreshEditorData}
        initialScorecardStepId={editorScorecardStepId}
      />

      <PromotePromptVersionDialog
        open={isPromotionDialogOpen}
        isPending={isPromotionPending}
        smartTableTitle={activeReference.smart_table_title}
        sheetTitle={activeReference.sheet_title}
        onOpenChange={setIsPromotionDialogOpen}
        onConfirm={() => void promoteCurrentPrompt()}
      />

      <EvaluatorBreakdownDialog
        open={isEvaluatorBreakdownOpen}
        onOpenChange={setIsEvaluatorBreakdownOpen}
        initialView={evaluatorBreakdownView}
        rows={displayedRunRows}
        runSummary={displayedRun?.summary}
        scorecardSnapshot={displayedRun?.scorecard_snapshot}
        sheetColumns={activeReference.columns}
        inputMappings={
          displayedRun?.input_variable_mappings_snapshot ?? activeReference.input_variable_mappings
        }
        templateOverrideColumnId={
          displayedRun?.template_override_snapshot?.column_id ??
          activeReference.template_override?.column_id
        }
        staleStepIds={displayedRunStaleState.staleStepIds}
      />
    </div>
  );
};

export default observer(Evaluate);
